<template>
  <v-container>
    <stats-buttons
      v-model="chartType"
      :sub-plots="subPlots"
      :variables="variables"
      @addChart="addChart"
      @error="showError"
    />

    <stats-results
      v-if="ready && chartType && chartData"
      :chart-type="chartType"
      :chart-data="chartData"
      :chart-headers="chartHeaders"
      class="pt-5"
    />

    <download-button
      v-if="ready"
      @download="downloadData"
    />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { errorMessages, labels } from '@/assets/texts.json';
import { isArrEmpty, removeDuplicates } from '@/helpers';
import statsService from '@/services/stats.service';

const DownloadButton = () => import('@/components/DownloadButton.vue');
const StatsButtons = () => import('@/components/StatsButtons.vue');
const StatsResults = () => import('@/components/StatsResults.vue');

export default {
  name: 'StatsView',
  metaInfo: {
    titleTemplate: `%s | ${labels.stats}`,
  },
  components: {
    DownloadButton,
    StatsButtons,
    StatsResults,
  },
  data() {
    return {
      backRoute: {
        name: 'home',
      },
      chartMethod: null,
      chartData: null,
      chartHeaders: null,
      chartType: null,
      errorMessages,
      labels,
      subPlots: null,
      variables: null,
    };
  },
  computed: {
    ...mapState('shared', [
      'download',
      'loading',
      'ready',
    ]),
    mobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  mounted() {
    this.setSectionTitle([
      labels.title,
      labels.stats,
    ]);
    this.setBackRoute(this.backRoute);
  },
  methods: {
    ...mapActions({
      setBackRoute: 'shared/setBackRoute',
      setSectionTitle: 'shared/setSectionTitle',
      showError: 'shared/errorMsg',
      toggleLoading: 'shared/toggleLoading',
      toggleReady: 'shared/toggleReady',
    }),
    async addChart(...chartArgs) {
      try {
        this.chartMethod = (...extraArgs) => {
          switch (this.chartType) {
            case 'yearly':
              return this.subplotYearly(...chartArgs, ...extraArgs);
            case 'daily':
              return this.subplotDaily(...chartArgs, ...extraArgs);
            default:
              return this.plotDaily(...chartArgs, ...extraArgs);
          }
        };
        this.chartData = await this.chartMethod();
        this.toggleReady(!isArrEmpty(this.chartData));
        if (!this.ready) {
          this.showError(this.errorMessages.noData);
        }
      } catch (error) {
        console.log(error);
        this.showError(this.errorMessages.chartError);
        this.chartData = null;
      }
    },
    downloadData() {
      this.toggleLoading(true);
      this.chartMethod('xlsx')
        .then(() => {
          console.log('got it');
        }).finally(() => {
          this.toggleLoading(false);
        });
    },
    plotDaily(plots, fromDate, untilDate, variables, ...extraArgs) {
      return statsService.getPlotDaily(
        plots.map((plot) => plot.id),
        fromDate,
        untilDate,
        variables.map((variable) => variable.id),
        ...extraArgs,
      );
    },
    subplotDaily(subPlots, variables, years, ...extraArgs) {
      return statsService.getSubplotDaily(
        removeDuplicates(subPlots, 'subplotLabel'),
        variables.map((variable) => variable.id),
        years,
        ...extraArgs,
      );
    },
    async subplotYearly(subPlots, variables, ...extraArgs) {
      this.chartHeaders = [
        labels.year,
        labels.plot,
        labels.crop,
        ...variables.map((variable) => (
          variable.units
            ? {
              text: `${variable.name} (${variable.units})`,
              value: variable.name,
            }
            : variable.name
        )),
      ];
      return statsService.getSubplotYearly(
        removeDuplicates(subPlots, 'subplotLabel'),
        variables.map((variable) => variable.id),
        ...extraArgs,
      );
    },
  },
  watch: {
    chartType() {
      this.chartData = null;
    },
  },

};
</script>

<style>
</style>
